/**
 * Égérie ref tracking — captures `?ref=<egerie_id>` from URL and persists it
 * for 30 days in localStorage. Used to attribute inquiries + sales to the égérie
 * that brought the customer.
 */
import axios from 'axios';

const STORAGE_KEY = 'egerieRef';
const TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const isValidUuid = (s) =>
  typeof s === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s);

/**
 * Read `?ref=` from current URL. If present + valid UUID, persist it and fire
 * a track/click beacon. Idempotent. Safe to call from anywhere on any route.
 */
export function captureRefFromUrl() {
  try {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');
    if (!ref || !isValidUuid(ref)) return null;

    // Try to derive the shop_id from the URL (`/makiti/shop/<uuid>`)
    let shopId = null;
    const match = window.location.pathname.match(/\/makiti\/shop\/([0-9a-f-]{36})/i);
    if (match) shopId = match[1];

    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      ref,
      shop_id: shopId,
      ts: Date.now(),
    }));

    // Fire-and-forget click tracking (deduped server-side within 24h)
    axios.post(`${API}/marketplace/track/click`, {
      egerie_id: ref,
      shop_id: shopId,
      source: document.referrer ? new URL(document.referrer).hostname : 'direct',
    }).catch(() => {});

    return ref;
  } catch {
    return null;
  }
}

/**
 * Returns the active égérie ref if not expired, else null.
 */
export function getActiveEgerieRef() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const { ref, ts } = JSON.parse(raw);
    if (!ref || !ts) return null;
    if (Date.now() - ts > TTL_MS) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return ref;
  } catch {
    return null;
  }
}

/**
 * Clear the ref (e.g., after conversion when we want a fresh window).
 */
export function clearEgerieRef() {
  try { localStorage.removeItem(STORAGE_KEY); } catch {}
}
